import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

// Ordre de référence des types de campagnes : il fixe la couleur de chacun, de sorte
// qu'un même type garde la même teinte d'un compte à l'autre et d'un rendu à l'autre.
// Exporté pour que le tableau puisse afficher des pastilles assorties aux camemberts.
export const CAMPAIGN_TYPE_ORDER = [
  'App (Android)',     // #8884d8 (bleu/violet)
  'App (iOS)',         // #82ca9d (vert)
  'DemandGen',         // #ffc658 (jaune) ⭐
  'Découverte',        // #ff7c7c (rouge/rose)
  'Display',           // #8dd1e1 (cyan)
  'Hôtel',            // #d084d0 (violet/rose)
  'Inconnu',          // #8884d8 (bleu/violet, cycle)
  'Intelligente',     // #82ca9d (vert, cycle)
  'Local',            // #ffc658 (jaune, cycle)
  'Multi-canal',      // #ff7c7c (rouge/rose, cycle)
  'Non défini',       // #8dd1e1 (cyan, cycle)
  'Non spécifié',     // #d084d0 (violet/rose, cycle)
  'Performance Max',   // #8884d8 (bleu/violet, cycle)
  'Recherche',        // #82ca9d (vert, cycle)
  'Services locaux',   // #ffc658 (jaune, cycle)
  'Shopping',         // #ff7c7c (rouge/rose, cycle)
  'Vidéo',           // #8dd1e1 (cyan, cycle)
  'Voyage'           // #d084d0 (violet/rose, cycle)
];

export const getCampaignTypeColor = (typeName, colors) => {
  const index = CAMPAIGN_TYPE_ORDER.indexOf(typeName);
  const position = index >= 0 ? index : CAMPAIGN_TYPE_ORDER.length;
  return colors[position % colors.length];
};

/**
 * Composant pour afficher les pie charts des types de campagnes
 * Clics par type et Conversions par type
 */
const CampaignTypesCharts = ({ 
  campaignsData, 
  getChannelTypeName, 
  COLORS 
}) => {
  
  // Fonction pour formater les nombres avec séparateurs de milliers
  const formatNumber = (num, decimals = 0) => {
    if (num == null || isNaN(num)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };
  
  // Grouper les données par type de campagne
  const typeGroups = {};
  campaignsData.forEach(campaign => {
    const type = getChannelTypeName(campaign.advertisingChannelType || campaign.type || 'Non spécifié');
    if (!typeGroups[type]) {
      typeGroups[type] = { clicks: 0, conversions: 0 };
    }
    typeGroups[type].clicks += campaign.clicks || 0;
    typeGroups[type].conversions += campaign.conversions || 0;
  });

  const colorMap = {};
  CAMPAIGN_TYPE_ORDER.forEach((typeName, index) => {
    colorMap[typeName] = COLORS[index % COLORS.length];
  });
  
  // Utiliser seulement les types présents dans les données actuelles
  const typeNames = Object.keys(typeGroups).sort();

  // Préparer les données pour les pie charts avec mapping de couleurs cohérent
  const clicksData = Object.entries(typeGroups).map(([type, data]) => ({
    name: type,
    value: data.clicks,
    color: colorMap[type],
    percentage: Object.values(typeGroups).reduce((sum, d) => sum + d.clicks, 0) > 0 ? 
      (data.clicks / Object.values(typeGroups).reduce((sum, d) => sum + d.clicks, 0) * 100).toFixed(1) : '0.0'
  })).filter(item => parseFloat(item.percentage) > 0.0);

  const conversionsData = Object.entries(typeGroups).map(([type, data]) => ({
    name: type,
    value: data.conversions,
    color: colorMap[type],
    percentage: Object.values(typeGroups).reduce((sum, d) => sum + d.conversions, 0) > 0 ? 
      (data.conversions / Object.values(typeGroups).reduce((sum, d) => sum + d.conversions, 0) * 100).toFixed(1) : '0.0'
  })).filter(item => parseFloat(item.percentage) > 0.0);

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px'
      }}
    >
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Pie chart Clics (gauche) */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par type de campagne</h5>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={clicksData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percentage }) => `${percentage}%`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {clicksData.map((entry, index) => (
                      <Cell key={`clicks-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [formatNumber(value) + ' clics', name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Légende commune (centre) */}
            <div style={{ 
              minWidth: '120px', 
              padding: '0 20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              {typeNames.map((type) => (
                <div key={type} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px',
                  fontSize: '13px'
                }}>
                  <div style={{ 
                    width: '12px', 
                    height: '12px', 
                    backgroundColor: colorMap[type], 
                    marginRight: '8px',
                    borderRadius: '2px'
                  }}></div>
                  <span>{type}</span>
                </div>
              ))}
            </div>
            
            {/* Pie chart Conversions (droite) */}
            <div style={{ flex: 1, textAlign: 'center' }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par type de campagne</h5>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={conversionsData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percentage }) => `${percentage}%`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {conversionsData.map((entry, index) => (
                      <Cell key={`conversions-cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [formatNumber(parseFloat(value), 1) + ' conversions', name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
    </div>
  );
};

export default CampaignTypesCharts;